/** @jsxImportSource @emotion/react */

import classNames from 'classnames';
import React from 'react';

export type UDTextType =
  | 'title-huge'
  | 'title-large'
  | 'title-1'
  | 'title-3'
  | 'headline'
  | 'body-text'
  | 'callout'
  | 'subhead'
  | 'footnote'
  | 'caption-1'
  | 'caption-2'
  | 'tip';

export type UDTextNewType =
  | 'title-1'
  | 'title-2'
  | 'title-3'
  | 'title-4'
  | 'subtitle-2'
  | 'subtitle-3'
  | 'subtitle-4'
  | 'subtitle-5'
  | 'body-1'
  | 'body-2'
  | 'body-3'
  | 'text-1'
  | 'label-1'
  | 'mini-bold'
  | 'big-size';

/**
 * Целевая шкала из макета (IMGM-1236), классы описаны в `src/styles/typography-scale.css`.
 * Имена совпадают с именами стилей в Figma — таблицы перевода между кодом и дизайном нет.
 */
export type UDTextVariant =
  | 'title-1'
  | 'title-2'
  | 'title-3'
  | 'title-4'
  | 'title-4-bold'
  | 'title-5'
  | 'title-6'
  | 'title-6-bold'
  | 'subtitle-1'
  | 'subtitle-1-bold'
  | 'subtitle-2-bold'
  | 'subtitle-4'
  | 'body-1'
  | 'body-2'
  | 'body-3'
  | 'text-1'
  | 'text-2'
  | 'text-2-bold'
  | 'text-3';

export type UDTextTag = 'p' | 'span' | 'div' | 'label' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const componentsMap: Record<UDTextType, keyof JSX.IntrinsicElements> = {
  'title-huge': 'h1',
  'title-large': 'h1',
  'title-1': 'h1',
  'title-3': 'h3',
  headline: 'h1',
  'body-text': 'p',
  callout: 'p',
  subhead: 'p',
  footnote: 'p',
  'caption-1': 'p',
  'caption-2': 'p',
  tip: 'p',
};

const newComponentsMap: Record<UDTextNewType, 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p'> = {
  'title-1': 'h1',
  'title-2': 'h3',
  'title-3': 'h2',
  'title-4': 'h1',
  'subtitle-2': 'p',
  'subtitle-3': 'p',
  'subtitle-4': 'p',
  'subtitle-5': 'p',
  'body-1': 'p',
  'body-2': 'p',
  'body-3': 'p',
  'text-1': 'p',
  'label-1': 'p',
  'mini-bold': 'p',
  'big-size': 'p',
};

type ComponentProps =
  | React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>
  | React.DetailedHTMLProps<React.HTMLAttributes<HTMLParagraphElement>, HTMLParagraphElement>;

type CommonProps = {
  maxWidth?: number;
  children: React.ReactNode;
  color?: string | 'inherit' | undefined;
  center?: boolean;
} & Omit<ComponentProps, 'children'>;

export type Props =
  | ({
      isNew?: false;
      type: UDTextType;
    } & CommonProps)
  | ({
      isNew: true;
      type: UDTextNewType;
    } & CommonProps)
  | ({
      /** Целевая шкала. Размер шрифта не диктует уровень заголовка — семантика задаётся через `as`. */
      variant: UDTextVariant;
      as?: UDTextTag;
      /** Межстрочный интервал для многострочного текста: в макете он равен кеглю. */
      relaxed?: boolean;
    } & CommonProps);

/**
 * Плоская проекция `Props` для разбора внутри компонента: ветки объединения не имеют общих
 * ключей, поэтому деструктурировать напрямую из `Props` нельзя. Наружу не выходит.
 */
type ResolvedProps = CommonProps & {
  type?: UDTextType | UDTextNewType;
  isNew?: boolean;
  variant?: UDTextVariant;
  as?: UDTextTag;
  relaxed?: boolean;
};

const UDText = React.forwardRef<HTMLElement, Props>((props, ref) => {
  const {
    type,
    variant,
    as,
    relaxed,
    children,
    className,
    color,
    maxWidth,
    isNew = false,
    center,
    ...otherProps
  } = props as ResolvedProps;

  const additionalStyle = maxWidth
    ? {
        maxWidth,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }
    : {};

  const legacyComponent = isNew ? newComponentsMap[type as UDTextNewType] : componentsMap[type as UDTextType];

  const Component = (variant ? (as ?? 'p') : legacyComponent) as keyof JSX.IntrinsicElements;

  const classes = variant
    ? classNames('typo', variant, className, { center, relaxed })
    : classNames({ text: !isNew, 'text-new': isNew }, type, className, { center });

  return React.createElement(
    Component,
    {
      ...otherProps,
      ref,
      className: classes,
      style: { color, ...additionalStyle, ...otherProps.style },
    },
    children,
  );
});

export default UDText;
